import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, FileText, Headset } from "lucide-react";
import "./mega-menu.css";

/**
 * Resources Mega Menu — "Engineering Resource Centre": a compact premium panel (≈1220px, ~500px tall), not the old
 * 8-equal-card grid. Structure: a short intro header, then a 35/65 split (a real Shingare photo as the featured panel on
 * the left, a two-column compact list of the 8 resource destinations on the right), then a slim expert-CTA strip.
 * Cards (title/description/icon/url) and the CTA copy are unchanged — still fully driven by Settings (Admin → Menus),
 * same as before; only the layout changed. Closes on Escape, outside click, or picking a link (unchanged behaviour).
 */
export default function ResourcesMegaMenu({ cards = [], cta = {}, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    function onClick(e) { if (ref.current && !ref.current.contains(e.target)) onClose?.(); }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [onClose]);

  const left = cards.slice(0, 4);
  const right = cards.slice(4, 8);

  return (
    <nav aria-label="Resources" data-testid="resources-mega-menu" className="absolute left-0 right-0 top-full z-50 flex justify-center px-4 pt-2">
      <div
        ref={ref}
        className="mm-drop w-full max-w-[1220px] overflow-hidden rounded-[20px] border border-[#DDE5F0] bg-white shadow-[0_30px_70px_-30px_rgba(7,27,58,0.5)]"
      >
        <div className="p-6 lg:p-7">
          <div className="mb-5 flex items-end justify-between gap-4 border-b border-[#E4E9F2] pb-4">
            <div className="mm-text-in">
              <div className="mb-1.5 flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#1769FF]"><span className="h-px w-6 bg-[#1769FF]" aria-hidden="true" />Engineering resource centre</div>
              <div className="font-heading text-[21px] font-semibold leading-tight tracking-[-0.02em] text-[#102A4C]">Technical Resources for Better Decisions</div>
              <p className="mt-1.5 hidden max-w-lg text-[13px] leading-relaxed text-[#667085] sm:block">Access product catalogues, technical datasheets, operating manuals, videos and engineering insights from Shingare Industries.</p>
            </div>
            <Link to="/resources" onClick={onClose} className="group inline-flex shrink-0 items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.1em] text-[#1769FF] hover:text-[#0F5CE6]">
              All resources
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid gap-6 lg:grid-cols-[300px_1fr] xl:grid-cols-[minmax(300px,35%)_1fr]">
            <FeaturedPanel onClose={onClose} />

            <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
              <ul className="space-y-0.5">{left.map((c, i) => <ResourceItem key={c.title || i} c={c} onClose={onClose} />)}</ul>
              <ul className="space-y-0.5">{right.map((c, i) => <ResourceItem key={c.title || i} c={c} onClose={onClose} />)}</ul>
            </div>
          </div>
        </div>

        {/* Bottom expert CTA */}
        <div className="flex flex-col items-start gap-3 border-t border-[#E4E9F2] bg-[#F3F7FD] px-6 py-4 sm:flex-row sm:items-center sm:justify-between lg:px-7">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-[#1769FF] shadow-sm" aria-hidden="true"><Headset className="h-[18px] w-[18px]" strokeWidth={1.7} /></span>
            <div>
              <div className="font-heading text-[13.5px] font-semibold leading-snug text-[#102A4C]">{cta.headline || "Need help finding the right technical information?"}</div>
              <div className="text-[12px] leading-snug text-[#667085]">{cta.text || "Our team can help you find the right product documentation."}</div>
            </div>
          </div>
          <Link
            to={cta.url || "/contact"}
            onClick={onClose}
            data-testid="rm-cta-button"
            className="group inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-[9px] bg-[#071B3A] px-5 py-2.5 text-[11.5px] font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#0F2A52]"
          >
            {cta.label || "Talk to Our Experts"}
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </nav>
  );
}

/** Static featured panel — a real Shingare application photograph (not per-item, unlike the Industries menu: resources aren't tied to one visual identity each). */
function FeaturedPanel({ onClose }) {
  return (
    <Link
      to="/resources"
      onClick={onClose}
      data-testid="rm-featured-panel"
      className="group relative hidden min-h-[300px] overflow-hidden rounded-2xl bg-[#071B3A] text-white lg:block"
    >
      <img
        src="/images/company/tube-cleaning-services.webp"
        alt="Shingare technician cleaning a heat-exchanger tube bundle on site"
        width="648"
        height="550"
        loading="lazy"
        decoding="async"
        className="mm-img-in absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,27,58,0.25)_0%,rgba(7,27,58,0.35)_35%,rgba(7,27,58,0.92)_100%)]" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-[#1769FF]/25 blur-[70px]" aria-hidden="true" />

      <div className="relative flex h-full min-h-[300px] flex-col justify-end p-6">
        <div className="mm-text-in">
          <div className="mb-2.5 flex items-center gap-2.5 text-[10.5px] font-semibold uppercase tracking-[0.2em] text-[#7FB0FF]"><span className="h-px w-6 bg-[#7FB0FF]" aria-hidden="true" />Engineering resources</div>
          <div className="font-heading text-[19px] font-semibold leading-[1.15] tracking-[-0.01em]">Explore Shingare&apos;s Technical Resources</div>
          <p className="mt-2 text-[12.5px] leading-relaxed text-white/75">Product information, technical documentation and engineering knowledge for industrial applications.</p>
          <span className="mt-4 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.1em] text-white">
            <span className="border-b border-white/35 pb-0.5 transition-colors group-hover:border-white">Explore resources</span>
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function ResourceItem({ c, onClose }) {
  const Icon = Icons[c.icon] || FileText;
  const to = c.url || "/resources";
  const isExternal = /^https?:\/\//i.test(to);
  const cls = "group relative flex items-start gap-3 rounded-xl px-2.5 py-2.5 transition-colors duration-200 hover:bg-[#F6F8FC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1769FF]";
  const inner = (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F1F4F9] text-[#1769FF] transition-colors duration-200 group-hover:bg-[#EEF4FF]"><Icon className="h-[18px] w-[18px]" strokeWidth={1.7} aria-hidden="true" /></span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="text-[13.5px] font-semibold leading-tight text-[#102A4C] transition-colors duration-200 group-hover:text-[#1769FF]">{c.title}</span>
          <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-transparent transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-[#1769FF]" aria-hidden="true" />
        </span>
        <span className="mt-0.5 line-clamp-1 block text-[12px] leading-snug text-[#667085]">{c.description}</span>
      </span>
    </>
  );
  return isExternal ? (
    <li><a href={to} target="_blank" rel="noreferrer" data-testid={`rm-item-${slug(c.title)}`} className={cls}>{inner}</a></li>
  ) : (
    <li><Link to={to} onClick={onClose} data-testid={`rm-item-${slug(c.title)}`} className={cls}>{inner}</Link></li>
  );
}

function slug(s = "") {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
